import _ from 'underscore';
import getConfig from 'next/config';
import qs from 'qs';
import { logInfo, logError } from '../logger';
import RequestPromise from './RequestPromise';

const { publicRuntimeConfig } = getConfig();
const { api } = publicRuntimeConfig.services;

export const prepareQueryParams = (requestParams) =>
	qs.stringify(requestParams, { arrayFormat: 'indices' });

const request = async (resourceName, data, method, params = {}) => {
	let apiConfig;
	let RequestAbortController;

	if (process.browser) {
		apiConfig = api.frontend;
		RequestAbortController = window.AbortController;
	} else {
		apiConfig = api.backend;
		const NodeAbortController = require('node-abort-controller');
		RequestAbortController = NodeAbortController.AbortController;
	}

	const requestParams = { ...data };
	const requestMethod = method || 'GET';

	let url = `${apiConfig.baseUrl}/${resourceName}`;

	const headers = {
		'Accept-Version': apiConfig.version,
		Connection: 'close',
		'Cache-Control': 'no-cache',
		Pragma: 'no-cache',
		...params.headers
	};

	if (params.cookies) {
		headers.Cookie = Object.entries(params.cookies)
			.map(([key, val]) => `${key}=${val}`)
			.join('; ');
	}

	const options = {
		method: requestMethod,
		credentials: 'include'
	};

	if (requestMethod.toLowerCase() === 'get') {
		if (!_(requestParams).isEmpty()) {
			const queryParams = prepareQueryParams(requestParams);
			url += `?${queryParams}`;
		}
	} else if (params && params.multipart) {
		const formData = new FormData();
		const { binary, ...restRequestParams } = requestParams;

		_(restRequestParams).each((val, key) => {
			formData.set(key, val);
		});

		_(binary).each((val, key) => {
			formData.append(key, val);
		});

		options.body = formData;
	} else {
		options.body = JSON.stringify(requestParams);
		headers['Content-Type'] = 'application/json';
	}

	options.headers = headers;

	const logPrefix = `${requestMethod.toUpperCase()} ${url.toString()}`;
	const isLog = publicRuntimeConfig.requestLogLevel !== 'none';

	if (isLog) {
		logInfo(`${logPrefix}: start requesting`);
	}

	let res;
	const startRequestDate = new Date();

	let timeoutId;

	if (apiConfig.timeout && RequestAbortController) {
		const controller = new RequestAbortController();

		options.signal = controller.signal;

		timeoutId = setTimeout(() => controller.abort(), apiConfig.timeout);
	}

	if (publicRuntimeConfig.env === 'development') {
		logInfo(`Sending request: ${url.toString()}`);
	}

	try {
		res = await fetch(url, options);

		if (res.status >= 400) {
			const error = new Error(
				`Response status from server: ${res.status} ${res.statusText}`
			);
			error.statusCode = res.status;
			throw error;
		} else if (isLog) {
			logInfo(`${logPrefix}: successful done requesting`);
		}
	} catch (err) {
		if (err.name === 'AbortError') {
			throw new Error(`Timeout exceeded for request url: ${url}`);
		}

		if (isLog) {
			const message = res ? `${res.status} ${res.statusText}` : err.message;
			logError(`${logPrefix}: error occured during request: ${message}`);
		}

		throw err;
	} finally {
		clearTimeout(timeoutId);

		if (isLog) {
			logInfo(
				`${logPrefix}: response time ${new Date() - startRequestDate} ms`
			);
		}
	}

	return res.status === 204 ? null : res.json();
};

export default (...args) => new RequestPromise(request, ...args);
